import clsx from 'clsx';
import React, { useEffect, useRef, useState } from 'react';
import { toast } from 'react-hot-toast';
import { useAlgo } from '../hooks/useAlgo';
import { useKittiesStore } from '../hooks/useKittiesStore';
import generateTransparentKitty from '../utils/generateTransparentKitty';
import Alert from './Alert';
import Button from './Button';
import ConnectWalletMessage from './ConnectWalletMessage';
import KittyPreview from './KittyPreview';
import Spinner from './Spinner';
import Steps from './Steps';

const MAX_KITTIES = 4;
const COLORS = ['#000000', '#171717', '#DC524C', '#E77B35', '#F0CA73', '#522082', '#845EEC', '#55B686', '#4E80ED', '#52B3D0'];

const TheBannersPage = () => {
  const bannerRef = useRef(null);
  const { address } = useAlgo();
  const { myKitties, myKittiesLoading } = useKittiesStore();
  const myFirstLitterKitties = myKitties?.filter((kitty) => kitty.gen === 1);
  const myFourthLitterKitties = myKitties?.filter((kitty) => kitty.gen === 4);
  const myKittiesForBuilder = myKitties;
  const [bannerKitties, setBannerKitties] = useState([]);
  const [selectedColor, setSelectedColor] = useState(COLORS[0]);
  const [step, setStep] = useState(0);
  const [showFeet, setShowFeet] = useState(false);

  const steps = [
    { id: 0, name: 'Select a background color', onClick: () => setStep(0) },
    { id: 1, name: 'Select a Shitty Kitty', onClick: () => setStep(1) },
  ];

  const checkIfSelected = (kitty) => {
    return bannerKitties.some((k) => k.id === kitty.id);
  };

  const onSelectKitty = async (kitty) => {
    const image = await generateTransparentKitty(kitty);

    // if kitty is already selected, remove it
    if (checkIfSelected(kitty)) {
      setBannerKitties((prev) => prev.filter((k) => k.id !== kitty.id));
    } else {
      if (bannerKitties.length >= MAX_KITTIES) {
        toast.error(`You can only add ${MAX_KITTIES} kitties to the banner`);
        return;
      }

      setBannerKitties([...bannerKitties, { id: kitty.id, image, gen: kitty.gen }]);
    }
  };

  const onSelectColor = (color) => {
    setSelectedColor(color);
    setStep(1);
  };

  const onDownloadCanvasImage = async () => {
    const canvas = bannerRef.current;
    const dataURL = canvas.toDataURL('image/png');
    const link = document.createElement('a');
    link.download = 'shitty-kitty-banner.png';
    link.href = dataURL;
    link.click();
  };

  useEffect(() => {
    if (!bannerRef.current?.getContext('2d')) return;

    // clear the canvas
    bannerRef.current.getContext('2d').clearRect(0, 0, 3000, 1000);

    // draw the background color
    bannerRef.current.getContext('2d').fillStyle = selectedColor;
    bannerRef.current.getContext('2d').fillRect(0, 0, 3000, 1000);

    const theImage = new Image();
    theImage.src = '/shitty-kitties-logo.png';
    theImage.width = 1000;
    theImage.height = 194;
    // in center
    const x = 1000;
    const y = 20;

    theImage.onload = () => {
      bannerRef.current.getContext('2d').drawImage(theImage, x, y, 1000, 194);
    };

    if (bannerKitties.length === 0) return;

    const canvasWidth = 3000;
    const count = bannerKitties.length;
    bannerKitties.forEach((bannerKitty, index) => {
      const theImage = new Image();
      theImage.src = bannerKitty.image;
      let width = 800;
      theImage.width = width;
      theImage.height = width;

      let x = 0;
      let y = 200 + index;

      if (count === 1) {
        x = canvasWidth / 2 - width / 2;
      } else if (count === 2) {
        x = canvasWidth / 2 - width / 2 + index * width - width / 2;
      } else if (count === 3) {
        x = canvasWidth / 2 - width / 2 + index * width - width;
      } else if (count === 4) {
        x = canvasWidth / 2 - width / 2 + (index * width - width * 1.5) / 1.1;
      }

      if (bannerKitty.gen === 1) {
        y = 200 + index + 50;
        width = 2500;
        x = x - 250;

        if (showFeet) {
          y = 200 + index + 50;
          width = 1600;
          x = x + 300;
        }
      }

      if (bannerKitty.gen === 4 && !showFeet) {
        y = 200 + index + 50;
        width = 1000;
        x = x - 100;
      }

      theImage.onload = () => {
        bannerRef.current.getContext('2d').drawImage(theImage, x, y, width, width);
      };
    });
  }, [bannerKitties.length, selectedColor, showFeet]);

  if (!address) {
    return (
      <>
        <div className='flex justify-center mt-8'>
          <ConnectWalletMessage />
        </div>
      </>
    );
  }

  if (myKittiesLoading) {
    return (
      <div className='flex justify-center mt-20'>
        <Spinner />
      </div>
    );
  }

  if (myKittiesForBuilder.length === 0) {
    return (
      <div className='max-w-lg mx-auto mt-10 text-center'>
        <Alert type='danger' className={'flex text-center justify-center'}>
          You must own a Shitty Kitty to use the Banner Builder.<br></br> Check out the{' '}
          <a href={`/marketplace`} className='font-bold'>
            Marketplace
          </a>{' '}
          to purchase a Shitty Kitty.
        </Alert>
      </div>
    );
  }

  if (step === 0) {
    return (
      <div className='mt-8'>
        <div className='rounded-lg bg-gray-900 overflow-hidden'>
          <canvas ref={bannerRef} className='w-full' width='3000' height='1000'></canvas>
        </div>

        <div className='mt-8 flex gap-10 justify-between items-center'>
          <div className='flex-grow-1 w-full'>
            <Steps steps={steps} activeStep={step} />
          </div>
        </div>

        <div className='mt-8'>
          <div className='grid grid-cols-2 md:grid-cols-4 lg:grid-cols-6 gap-6 mt-5'>
            {COLORS?.map((color) => (
              <button
                type='button'
                key={color}
                style={{ backgroundColor: color }}
                className={clsx('rounded-lg w-full aspect-square', { 'ring-4 ring-gray-100': setSelectedColor === color })}
                onClick={() => onSelectColor(color)}
              >
                <span className='sr-only'>{color}</span>
              </button>
            ))}
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className='mt-8'>
      <div className='rounded-lg bg-gray-900 overflow-hidden'>
        <canvas ref={bannerRef} className='w-full' width='3000' height='1000'></canvas>
      </div>

      <div className='mt-8 flex gap-10 justify-between items-center md:flex-row flex-col'>
        <div className='flex-grow-1 w-full'>
          <Steps steps={steps} activeStep={step} />
        </div>

        {bannerKitties.length > 0 && (
          <div>
            <Button onClick={onDownloadCanvasImage}>Download Banner</Button>
          </div>
        )}
      </div>

      {(myFirstLitterKitties.length > 0 || myFourthLitterKitties.length > 0) && (
        <div className='mt-8 flex items-center gap-2'>
          <input type='checkbox' id='showFeet' name='showFeet' checked={showFeet} onChange={() => setShowFeet(!showFeet)} />
          <label htmlFor='showFeet'>Show feet</label>
        </div>
      )}

      <div className='mt-8'>
        <div className='grid grid-cols-2 md:grid-cols-4 lg:grid-cols-6 gap-6'>
          {myKittiesForBuilder?.map((kitty) => (
            <button
              type='button'
              key={kitty.id}
              className={clsx('rounded-lg', { 'ring-4 ring-gray-100': checkIfSelected(kitty) })}
              onClick={() => onSelectKitty(kitty)}
            >
              <KittyPreview imageOnly rounded kitty={kitty} key={kitty.id} preventDefault />
            </button>
          ))}
        </div>
      </div>
    </div>
  );
};

export default TheBannersPage;
